import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';
import CircularProgress from '@mui/material/CircularProgress';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import FlightIcon from '@mui/icons-material/Flight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import {
    Stack,
    Typography,
    Skeleton,
    Modal,
    Tabs,
    Tab,
} from '@mui/material';

import {
    DashboardContainer,
    KpiGrid,
    PrimaryChartsGrid,
    SecondaryChartsGrid,
    SectionContainer,
    SectionTitle,
    SectionContainerHalf,
    SectionChevron,
    SectionContent,
    AreaChartsRow,
    InProgressMessage,
    TooltipContainer,
    ModalContent,
} from './DashboardIndicatorsStyle';

import DashboardFilterBar from '../../components/DashboardFilterBar/DashboardFilterBar';
import KpiCard from '../../components/KpiCard/KpiCard';
import ChartPanel from '../../components/ChartPanel/ChartPanel';
import NoDataMessage from '../../components/NoDataMessage/NoDataMessage';
import { API_BASE_URL } from '../../utils/config';
import Loader from '../../components/Loader/Loader';
import TablaIndicadoresMaleza from '../../components/TablaIndicadoresMaleza/TablaIndicadoresMaleza';
import Tutorial from '../../components/Tutorial/Tutorial';
import useDashboardData, { AREA_INDICATORS } from '../../hooks/useDashboardData';
import CardVisibilityDrawer from '../../components/CardVisibilityDrawer/CardVisibilityDrawer';
import brandLogo from '../../assets/img/logo_oddis_horizontal.png';

/* ═══════════════════════════════════════════════════════════ */
/*  Framer-motion variants                                     */
/* ═══════════════════════════════════════════════════════════ */
const fadeInUp = {
    hidden:  { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1, y: 0,
        transition: { delay: i * 0.05, duration: 0.35, ease: 'easeOut' },
    }),
};

const sectionFade = {
    hidden:  { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

const crossFade = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.25 } },
    exit:    { opacity: 0, transition: { duration: 0.15 } },
};

/* ═══════════════════════════════════════════════════════════ */
/*  DraggableCard — reorder within a named group via react-dnd */
/* ═══════════════════════════════════════════════════════════ */
const DraggableCard = ({ id, groupName, localIndex, orderType, itemType, onMove, canEdit, children }) => {
    const ref = useRef(null);

    const [{ isOver }, drop] = useDrop({
        accept: itemType,
        canDrop: (item) => canEdit && item.groupName === groupName,
        hover: (item) => {
            if (!canEdit) return;
            if (item.localIndex === localIndex) return;
            if (item.groupName === groupName) {
                onMove(orderType, groupName, item.localIndex, localIndex);
                item.localIndex = localIndex;
            }
        },
        collect: (monitor) => ({ isOver: monitor.isOver() }),
    });

    const [{ isDragging }, drag] = useDrag({
        type: itemType,
        item: { id, localIndex, groupName },
        canDrag: () => canEdit,
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    drag(drop(ref));
    return <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>{children}</div>;
};

/* ═══════════════════════════════════════════════════════════ */
/*  Constants                                                  */
/* ═══════════════════════════════════════════════════════════ */
const ITEM_TYPE_DATA_CARD = 'DATA_CARD';
const ITEM_TYPE_CHART_CARD = 'CHART_CARD';
const activitiesInProgress = ['HERBICIDAS', 'SIEMBRA'];

const SECTION_TITLE_MAP = {
    Totales: 'Totales',
    Promedios: 'Promedios',
    Dosis: 'Dosis',
    VelocidadRPM: 'Indicadores de Velocidad',
    PilotoAutomatico: 'Indicadores de Piloto Automático',
    CorteBase: 'Indicadores de Corte Base',
    IndicadoresVarios: 'Indicadores varios',
    IndicadoresGenerales: 'Indicadores generales',
    Indicadores: 'Indicadores',
};

/* Filter adapter — maps hook's activity-specific keys to DashboardFilterBar's generic keys */
const FILTER_KEY_MAP = {
    COSECHA_MECANICA: {
        toGeneric: { operadorCM: 'operadores', fincaCM: 'fincas', zafraCM: 'zafras', tercioCM: 'tercios', loteCM: 'lotes', usuarioEmpresa: 'usuarios' },
        fromGeneric: { operadores: 'operadorCM', fincas: 'fincaCM', zafras: 'zafraCM', tercios: 'tercioCM', lotes: 'loteCM', usuarios: 'usuarioEmpresa' },
    },
    APLICACIONES_AEREAS: {
        toGeneric: { operadorAA: 'responsables', fincaAA: 'fincas', productoAA: 'productos', zafraAA: 'zafras', loteAA: 'lotes', tercioAA: 'tercios', tipoAeronave: 'equipos', tipoProductoAA: 'tiposProducto', usuarioEmpresa: 'usuarios' },
        fromGeneric: { responsables: 'operadorAA', fincas: 'fincaAA', productos: 'productoAA', zafras: 'zafraAA', lotes: 'loteAA', tercios: 'tercioAA', equipos: 'tipoAeronave', tiposProducto: 'tipoProductoAA', usuarios: 'usuarioEmpresa' },
    },
    FERTILIZACION: {
        toGeneric: { operadorFR: 'operadores', fincaFR: 'fincas', productoFR: 'productos', zafraFR: 'zafras', tercioFR: 'tercios', usuarioEmpresa: 'usuarios' },
        fromGeneric: { operadores: 'operadorFR', fincas: 'fincaFR', productos: 'productoFR', zafras: 'zafraFR', tercios: 'tercioFR', usuarios: 'usuarioEmpresa' },
    },
    DETECCION_MALEZA: {
        toGeneric: { fincaDM: 'fincas', zafraDM: 'zafras', tercioDM: 'tercios', loteDM: 'lotes', usuarioEmpresa: 'usuarios' },
        fromGeneric: { fincas: 'fincaDM', zafras: 'zafraDM', tercios: 'tercioDM', lotes: 'loteDM', usuarios: 'usuarioEmpresa' },
    },
};

const CHART_STYLES = {
    'metricas-finca':                    { color: '#4caf50', icon: <AgricultureIcon /> },
    'porcentajes-finca':                 { colors: ['#81c784', '#4caf50', '#66bb6a'], icon: <AgricultureIcon /> },
    'metricas-operador':                 { color: '#ff9800', icon: <PersonIcon /> },
    'porcentajes-operador':              { colors: ['#ffb74d', '#ffa726', '#fb8c00'], icon: <PersonIcon /> },
    'metricas-maquina':                  { color: '#2196f3', icon: <BuildIcon /> },
    'porcentajes-maquina':               { colors: ['#64b5f6', '#42a5f5', '#1e88e5'], icon: <BuildIcon /> },
    'totales-indicadores-producto':      { color: '#e91e63', icon: <ShowChartIcon /> },
    'metricas-producto':                 { color: '#e91e63', icon: <ShowChartIcon /> },
    'totales-indicadores-operador':      { color: '#ff9800', icon: <PersonIcon /> },
    'metricas-operador-aa':              { color: '#ff9800', icon: <PersonIcon /> },
    'totales-indicadores-zafra':         { color: '#795548', icon: <AccessTimeIcon /> },
    'metricas-zafra':                    { color: '#795548', icon: <AccessTimeIcon /> },
    'totales-indicadores-lote':          { color: '#9e9e9e', icon: <ShowChartIcon /> },
    'metricas-lote':                     { color: '#9e9e9e', icon: <ShowChartIcon /> },
    'totales-indicadores-tercio':        { color: '#607d8b', icon: <ShowChartIcon /> },
    'metricas-tercio':                   { color: '#607d8b', icon: <ShowChartIcon /> },
    'promedio-indicadores':              { color: '#9c27b0', icon: <FlightIcon /> },
    'tiempo-total':                      { color: '#f44336', icon: <AccessTimeIcon /> },
    'teorica-real':                      { color: '#009688', icon: <ShowChartIcon /> },
    'totales-indicadores':               { color: '#4a148c', icon: <FlightIcon /> },
    'totales-indicadores-finca':         { color: '#00695c', icon: <AgricultureIcon /> },
    'metricas-finca-dm':                 { color: '#8bc34a', icon: <AgricultureIcon /> },
    'totales-indicadores-finca-dm':      { color: '#689f38', icon: <AgricultureIcon /> },
    'metricas-zafra-dm':                 { color: '#ff5722', icon: <AccessTimeIcon /> },
    'totales-indicadores-zafra-dm':      { color: '#d84315', icon: <AccessTimeIcon /> },
    'metricas-tercio-dm':                { color: '#607d8b', icon: <ShowChartIcon /> },
    'totales-indicadores-tercio-dm':     { color: '#37474f', icon: <ShowChartIcon /> },
    'alturasPorVuelo':                   { color: '#2563EB', icon: <FlightIcon /> },
    'litrosPorVuelo':                    { color: '#16a34a', icon: <FlightIcon /> },
    'litrosHaPorVuelo':                  { color: '#dc2626', icon: <FlightIcon /> },
};

const FILTER_TITLES = {
    fechaInicio: 'Fecha Inicio', fechaFin: 'Fecha Fin',
    tipoAeronave: 'Tipo de Aeronave',
    tipoProductoAA: 'Tipo de producto',
    operadorAA: 'Operador', fincaAA: 'Finca', productoAA: 'Producto',
    zafraAA: 'Zafra', loteAA: 'Lote', tercioAA: 'Tercio',
    zafraCM: 'Zafra', tercioCM: 'Tercio', fincaCM: 'Finca',
    operadorCM: 'Operador', loteCM: 'Lote',
    operadorFR: 'Operador', fincaFR: 'Finca', productoFR: 'Producto',
    zafraFR: 'Zafra', tercioFR: 'Tercio',
    fincaDM: 'Finca', zafraDM: 'Zafra', tercioDM: 'Tercio', loteDM: 'Lote',
    usuarioEmpresa: 'Usuario',
};

const AREA_COLORS = ['#668c24', '#B22222', '#FFDB58'];

// VRA permanece como una sección adicional dentro de Aplicaciones Aéreas.
// Sus gráficas reutilizan exactamente los filtros superiores del módulo.
const VRA_DIMENSION_DEFINITIONS = [
    { key: 'finca', label: 'Finca' },
    { key: 'responsable', label: 'Responsable' },
    { key: 'producto', label: 'Producto' },
    { key: 'zafra', label: 'Zafra' },
    { key: 'lote', label: 'Lote' },
    { key: 'tercio', label: 'Tercio' },
    { key: 'equipo', label: 'Equipo' },
    { key: 'tipoProducto', label: 'Tipo de producto' },
];

const VRA_METRIC_DEFINITIONS = [
    // Conserva el mismo patrón visual del dashboard convencional de
    // Aplicaciones Aéreas: series dimensionales en línea y porcentajes en pie.
    { key: 'areaLoteHa', title: 'Área del lote', unit: 'ha', type: 'line', color: '#64748b' },
    { key: 'areaMalezaHa', title: 'Área con maleza', unit: 'ha', type: 'line', color: '#2e7d32' },
    { key: 'areaAplicadaEnMalezaHa', title: 'Área aplicada en maleza', unit: 'ha', type: 'line', color: '#16a34a' },
    { key: 'areaAplicadaSinMalezaHa', title: 'Área aplicada sin maleza', unit: 'ha', type: 'line', color: '#d97706' },
    { key: 'areaNoAplicadaEnMalezaHa', title: 'Área no aplicada en maleza', unit: 'ha', type: 'line', color: '#dc2626' },
    { key: 'precisionAplicacionPct', title: '% de precisión de aplicación', unit: '%', type: 'pie' },
];

/* ═══════════════════════════════════════════════════════════ */
/*  DashboardIndicators — Main Page                            */
/* ═══════════════════════════════════════════════════════════ */
const DashboardIndicators = ({ isSidebarOpen }) => {
    const {
        isViewer, canEdit, effectiveUserId,
        selectedActivity, handleActivitySelect,
        isAplicacionesAereas, isFertilizacion, isDeteccionMaleza, isCosechaMecanica,
        filtersCM, filtersAA, filtersFR, filtersDM, companyUsers,
        selectedFilters, dateRange, handleDateChange, handleFilterClick,
        loading, isOrderLoading,
        dataCardsOrder, chartCardsOrder, orderActivity, moveCardInGroup,
        hiddenCards, saveHiddenCards,
        promedioIndicadores, porcentajes,
        metricasMaquina, porcentajesMaquina,
        metricasOperadorCM, porcentajesOperadorCM,
        metricasFinca, porcentajesFinca,
        tiempoTotal, dosisTeoricaReal,
        totalesIndicadores, totalesIndicadoresFinca,
        totalesIndicadoresProducto, metricasProducto,
        totalesIndicadoresOperador, metricasOperador,
        totalesIndicadoresZafra, metricasZafra,
        totalesIndicadoresLote, metricasLote,
        totalesIndicadoresTercio, metricasTercio,
        metricasFincaDM, totalesIndicadoresFincaDM,
        metricasZafraDM, totalesIndicadoresZafraDM,
        metricasTercioDM, totalesIndicadoresTercioDM,
        lotesDMData, totalAnalisisCount, vraResumen, vraMetricasDimensiones,
        distAreasPromData, areaNoProdPromData,
        distAreasTotalData, areaNoProdTotalData,
        formatValue, getMinMaxFromLimits,
        groupDataCardsByCategory, groupChartCardsByCategory,
        mapChartData, mapChartDataForTotalsFinca, mapChartDataForTotalsGeneric,
    } = useDashboardData();

    /* ── Local UI state ── */
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [tooltip, setTooltip] = useState({ visible: false, content: '', position: { top: 0, left: 0 } });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isTutorialActive, setIsTutorialActive] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState({});
    const [visibilityDrawerOpen, setVisibilityDrawerOpen] = useState(false);
    const [aaDashboardView, setAaDashboardView] = useState('normal');
    const chartRefs = useRef({});
    const areaChartRefs = useRef({});

    // Aplicaciones Aéreas siempre abre en su vista convencional. VRA se
    // consulta mediante una pestaña separada para no saturar el dashboard.
    useEffect(() => {
        setAaDashboardView('normal');
    }, [selectedActivity]);

    const isVraDashboardView = isAplicacionesAereas && aaDashboardView === 'vra';

    const ACTIVITY_LABELS = {
        COSECHA_MECANICA: 'Cosecha Mecánica',
        APLICACIONES_AEREAS: 'Aplicaciones Aéreas',
        FERTILIZACION: 'Fertilización',
        DETECCION_MALEZA: 'Detección de Maleza',
    };
    const activityLabel = ACTIVITY_LABELS[selectedActivity] || selectedActivity;

    // Apartado independiente para VRA dentro del Dashboard Indicadores de
    // Aplicaciones Aéreas. No reemplaza ni altera las tarjetas convencionales.
    const vraDashboardCards = useMemo(() => {
        if (!isAplicacionesAereas || !vraResumen || Number(vraResumen.totalAnalisisVra || 0) <= 0) return [];
        return [
            { id: 'vra-area-lote', title: 'Área del lote', value: vraResumen.areaLoteHa, unit: 'ha' },
            { id: 'vra-area-maleza', title: 'Área con maleza', value: vraResumen.areaMalezaHa, unit: 'ha' },
            { id: 'vra-aplicada-maleza', title: 'Área aplicada en maleza', value: vraResumen.areaAplicadaEnMalezaHa, unit: 'ha' },
            { id: 'vra-aplicada-sin-maleza', title: 'Área aplicada sin maleza', value: vraResumen.areaAplicadaSinMalezaHa, unit: 'ha' },
            { id: 'vra-no-aplicada-maleza', title: 'Área no aplicada en maleza', value: vraResumen.areaNoAplicadaEnMalezaHa, unit: 'ha' },
            { id: 'vra-precision-aplicacion', title: '% de precisión de aplicación', value: vraResumen.precisionAplicacionPct, unit: '%' },
        ];
    }, [isAplicacionesAereas, vraResumen]);

    const vraDashboardChartSections = useMemo(() => {
        if (!isAplicacionesAereas || vraDashboardCards.length === 0) return [];

        return VRA_DIMENSION_DEFINITIONS.map((dimension) => {
            const rows = Array.isArray(vraMetricasDimensiones?.[dimension.key])
                ? vraMetricasDimensiones[dimension.key].filter((row) => String(row?.name || '').trim())
                : [];

            if (rows.length === 0) return null;

            return {
                ...dimension,
                charts: VRA_METRIC_DEFINITIONS.map((metric) => ({
                    ...metric,
                    id: `vra-${dimension.key}-${metric.key}`,
                    data: rows.map((row) => {
                        const value = Number(row?.[metric.key]);
                        return {
                            name: String(row?.name || '').trim(),
                            value: Number.isFinite(value) ? value : 0,
                        };
                    }),
                })),
            };
        }).filter(Boolean);
    }, [isAplicacionesAereas, vraDashboardCards.length, vraMetricasDimensiones]);

    const toggleSection = useCallback((key) => {
        setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    /* ═══════════════════════════════════════════════════════════ */
    /*  Filter bridge: hook ↔ DashboardFilterBar                   */
    /* ═══════════════════════════════════════════════════════════ */
    const usuarioOptions = useMemo(() => companyUsers.map((u) => u.EMAIL), [companyUsers]);

    const filterBarOptions = useMemo(() => {
        switch (selectedActivity) {
            case 'COSECHA_MECANICA':
                return {
                    operadores: filtersCM.operadorOptions || [],
                    fincas: filtersCM.fincasOptions || [],
                    zafras: filtersCM.zafrasOptions || [],
                    tercios: filtersCM.tercioOptions || [],
                    lotes: filtersCM.loteOptions || [],
                    usuarios: usuarioOptions,
                };
            case 'APLICACIONES_AEREAS':
                return {
                    responsables: filtersAA.operadorOptionsAA || [],
                    fincas: filtersAA.fincasOptionsAA || [],
                    productos: filtersAA.productosOptionsAA || [],
                    zafras: filtersAA.zafraOptionsAA || [],
                    lotes: filtersAA.loteOptionsAA || [],
                    equipos: ['Dron', 'Avioneta', 'Helicóptero'],
                    tiposProducto: ['Líquido', 'Sólido'],
                    usuarios: usuarioOptions,
                };
            case 'FERTILIZACION':
                return {
                    operadores: filtersFR.operadorOptionsFR || [],
                    fincas: filtersFR.fincaOptionsFR || [],
                    productos: filtersFR.productosOptionsFR || [],
                    zafras: filtersFR.zafraOptionsFR || [],
                    tercios: filtersFR.tercioOptionsFR || [],
                    usuarios: usuarioOptions,
                };
            case 'DETECCION_MALEZA':
                return {
                    fincas: filtersDM.fincasOptionsDM || [],
                    zafras: filtersDM.zafraOptionsDM || [],
                    tercios: filtersDM.tercioOptionsDM || [],
                    lotes: filtersDM.loteOptionsDM || [],
                    usuarios: usuarioOptions,
                };
            default:
                return {};
        }
    }, [selectedActivity, filtersCM, filtersAA, filtersFR, filtersDM, usuarioOptions]);

    /* Hook's selectedFilters { fincaCM: 'x' } → generic { fincas: ['x'] } */
    const genericSelectedFilters = useMemo(() => {
        const mapping = FILTER_KEY_MAP[selectedActivity]?.toGeneric || {};
        const result = {};
        Object.entries(selectedFilters).forEach(([key, val]) => {
            const genericKey = mapping[key];
            if (genericKey && val) {
                result[genericKey] = [val];
            }
        });
        return result;
    }, [selectedFilters, selectedActivity]);

    /* ── Staged filters: chips update local state, "Filtrar" button applies ── */
    const [stagedGenericFilters, setStagedGenericFilters] = useState({});

    const handleFiltersChange = useCallback((newGenericFilters) => {
        setStagedGenericFilters(newGenericFilters);
    }, []);

    const handleApplyFilters = useCallback(() => {
        const mapping = FILTER_KEY_MAP[selectedActivity]?.fromGeneric || {};
        const hookFilters = {};
        Object.entries(stagedGenericFilters).forEach(([genKey, values]) => {
            const actKey = mapping[genKey];
            if (actKey && values && values.length > 0) {
                hookFilters[actKey] = values[values.length - 1];
            }
        });
        handleFilterClick(hookFilters);
    }, [selectedActivity, stagedGenericFilters, handleFilterClick]);

    const handleFilterReset = useCallback(() => {
        setStagedGenericFilters({});
        handleFilterClick({});
    }, [handleFilterClick]);

    /* date bridge: DashboardFilterBar { start, end } → hook { startDate, endDate } */
    const filterBarDateRange = useMemo(() => ({
        start: dateRange?.startDate || null,
        end: dateRange?.endDate || null,
    }), [dateRange]);

    const handleDateRangeChange = useCallback((newRange) => {
        handleDateChange({
            startDate: newRange.start,
            endDate: newRange.end,
        });
    }, [handleDateChange]);

    /* ═══════════════════════════════════════════════════════════ */
    /*  Lookup card value — searches all data arrays               */
    /* ═══════════════════════════════════════════════════════════ */
    const lookupCardValue = useCallback((cardId) => {
        return (
            promedioIndicadores[0]?.[cardId]?.value ??
            porcentajes[0]?.[cardId]?.value ??
            metricasMaquina[0]?.[cardId]?.value ??
            porcentajesMaquina[0]?.[cardId]?.value ??
            metricasOperadorCM[0]?.[cardId]?.value ??
            porcentajesOperadorCM[0]?.[cardId]?.value ??
            metricasFinca[0]?.[cardId]?.value ??
            porcentajesFinca[0]?.[cardId]?.value ??
            tiempoTotal[0]?.[cardId]?.value ??
            dosisTeoricaReal[0]?.[cardId]?.value ??
            totalesIndicadores[0]?.[cardId]?.value ??
            totalesIndicadoresFinca[0]?.[cardId]?.value ??
            totalesIndicadoresProducto[0]?.[cardId]?.value ??
            metricasProducto[0]?.[cardId]?.value ??
            totalesIndicadoresOperador[0]?.[cardId]?.value ??
            metricasOperador[0]?.[cardId]?.value ??
            totalesIndicadoresZafra[0]?.[cardId]?.value ??
            metricasZafra[0]?.[cardId]?.value ??
            totalesIndicadoresLote[0]?.[cardId]?.value ??
            metricasLote[0]?.[cardId]?.value ??
            totalesIndicadoresTercio[0]?.[cardId]?.value ??
            metricasTercio[0]?.[cardId]?.value ??
            metricasFincaDM[0]?.[cardId]?.value ??
            totalesIndicadoresFincaDM[0]?.[cardId]?.value ??
            metricasZafraDM[0]?.[cardId]?.value ??
            totalesIndicadoresZafraDM[0]?.[cardId]?.value ??
            metricasTercioDM[0]?.[cardId]?.value ??
            totalesIndicadoresTercioDM[0]?.[cardId]?.value
        );
    }, [
        promedioIndicadores, porcentajes, metricasMaquina, porcentajesMaquina,
        metricasOperadorCM, porcentajesOperadorCM, metricasFinca, porcentajesFinca,
        tiempoTotal, dosisTeoricaReal, totalesIndicadores, totalesIndicadoresFinca,
        totalesIndicadoresProducto, metricasProducto, totalesIndicadoresOperador, metricasOperador,
        totalesIndicadoresZafra, metricasZafra, totalesIndicadoresLote, metricasLote,
        totalesIndicadoresTercio, metricasTercio, metricasFincaDM, totalesIndicadoresFincaDM,
        metricasZafraDM, totalesIndicadoresZafraDM, metricasTercioDM, totalesIndicadoresTercioDM,
    ]);

    /* ═══════════════════════════════════════════════════════════ */
    /*  Lookup chart data — searches all chart data arrays         */
    /* ═══════════════════════════════════════════════════════════ */
    const lookupChartData = useCallback((cardId) => {
        let data = mapChartData(metricasMaquina, porcentajesMaquina, cardId);
        if (!data.length) data = mapChartData(metricasOperadorCM, porcentajesOperadorCM, cardId);
        if (!data.length) data = mapChartData(metricasFinca, porcentajesFinca, cardId);
        if (!data.length) data = mapChartData(metricasProducto, [], cardId);
        if (!data.length) data = mapChartData(metricasOperador, [], cardId);
        if (!data.length) data = mapChartData(metricasZafra, [], cardId);
        if (!data.length) data = mapChartData(metricasLote, [], cardId);
        if (!data.length) data = mapChartData(metricasTercio, [], cardId);
        if (!data.length) data = mapChartData(metricasFincaDM, [], cardId);
        if (!data.length) data = mapChartData(metricasZafraDM, [], cardId);
        if (!data.length) data = mapChartData(metricasTercioDM, [], cardId);
        if (!data.length && totalesIndicadoresFinca.length) data = mapChartDataForTotalsFinca(totalesIndicadoresFinca, cardId);
        if (!data.length && totalesIndicadoresProducto.length) data = mapChartDataForTotalsGeneric(totalesIndicadoresProducto, cardId, '_id');
        if (!data.length && totalesIndicadoresOperador.length) data = mapChartDataForTotalsGeneric(totalesIndicadoresOperador, cardId, '_id');
        if (!data.length && totalesIndicadoresZafra.length) data = mapChartDataForTotalsGeneric(totalesIndicadoresZafra, cardId, '_id');
        if (!data.length && totalesIndicadoresLote.length) data = mapChartDataForTotalsGeneric(totalesIndicadoresLote, cardId, '_id');
        if (!data.length && totalesIndicadoresTercio.length) data = mapChartDataForTotalsGeneric(totalesIndicadoresTercio, cardId, '_id');
        if (!data.length && totalesIndicadoresFincaDM.length) data = mapChartDataForTotalsFinca(totalesIndicadoresFincaDM, cardId);
        if (!data.length && totalesIndicadoresZafraDM.length) data = mapChartDataForTotalsGeneric(totalesIndicadoresZafraDM, cardId, '_id');
        if (!data.length && totalesIndicadoresTercioDM.length) data = mapChartDataForTotalsGeneric(totalesIndicadoresTercioDM, cardId, '_id');
        return data;
    }, [
        metricasMaquina, porcentajesMaquina, metricasOperadorCM, porcentajesOperadorCM,
        metricasFinca, porcentajesFinca, metricasProducto, metricasOperador,
        metricasZafra, metricasLote, metricasTercio,
        metricasFincaDM, metricasZafraDM, metricasTercioDM,
        totalesIndicadoresFinca, totalesIndicadoresProducto, totalesIndicadoresOperador,
        totalesIndicadoresZafra, totalesIndicadoresLote, totalesIndicadoresTercio,
        totalesIndicadoresFincaDM, totalesIndicadoresZafraDM, totalesIndicadoresTercioDM,
        mapChartData, mapChartDataForTotalsFinca, mapChartDataForTotalsGeneric,
    ]);

    /* ═══════════════════════════════════════════════════════════ */
    /*  PDF Report Generation                                      */
    /* ═══════════════════════════════════════════════════════════ */
    const convertImageToBase64 = (url) => new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const c = document.createElement('canvas');
            c.width = img.width; c.height = img.height;
            c.getContext('2d').drawImage(img, 0, 0);
            resolve(c.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = url;
    });

    const handleGenerateReport = async () => {
        try {
            if (!canEdit) { toast.info('Tu usuario es visualizador: no puedes generar reportes.'); return; }
            setIsGeneratingReport(true);

            if (dataCardsOrder.length === 0 || chartCardsOrder.length === 0 || loading) {
                toast.warn('No hay datos para generar el reporte.'); return;
            }

            const now = moment();
            let logoBase64 = '';
            try { logoBase64 = await convertImageToBase64(brandLogo); }
            catch { toast.error('Error al cargar el logo para el reporte.'); return; }

            const reportData = {
                activity: selectedActivity,
                filters: {},
                dataCards: [],
                charts: [],
                dateTime: now.format('dddd, D [de] MMMM [de] YYYY, h:mm A'),
                logo: logoBase64,
            };

            for (const [key, value] of Object.entries(selectedFilters)) {
                if (value != null && value !== '') reportData.filters[FILTER_TITLES[key] || key] = value;
            }

            dataCardsOrder.filter((c) => !hiddenCards[c.id]).forEach((card) => {
                const dataValue = lookupCardValue(card.id);
                reportData.dataCards.push({
                    id: card.id,
                    title: card.title,
                    value: dataValue != null ? formatValue(dataValue) : 'Sin datos',
                    dimensional: resolveAADimensional(card),
                });
            });

            // Only capture chart refs that belong to the current activity's chart cards
            const currentChartIds = new Set(chartCardsOrder.map(c => c.id));
            const chartImages = [];
            const skippedCharts = [];
            for (const [cardId, chartRef] of Object.entries(chartRefs.current)) {
                if (!currentChartIds.has(cardId)) { skippedCharts.push({ id: cardId, reason: 'stale ref (not in current order)' }); continue; }
                if (hiddenCards[cardId]) { skippedCharts.push({ id: cardId, reason: 'hidden' }); continue; }
                if (chartRef?.current?.getDataURL) {
                    try {
                        const url = chartRef.current.getDataURL({ type: 'png', pixelRatio: 3, backgroundColor: '#fff' });
                        if (url) chartImages.push({
                            id: cardId,
                            title: resolveAAChartTitle(chartCardsOrder.find(c => c.id === cardId)) || cardId,
                            image: url,
                        }); else { skippedCharts.push({ id: cardId, reason: 'getDataURL returned empty (no chart data)' }); }
                    } catch (e) { skippedCharts.push({ id: cardId, reason: `error: ${e.message}` }); console.error('Error capturando gráfica:', e); }
                } else { skippedCharts.push({ id: cardId, reason: 'ref not ready' }); }
            }
            // Capture area charts (Distribución de Áreas / Área No Productiva) for AA/Fertilización
            for (const [areaId, areaRef] of Object.entries(areaChartRefs.current)) {
                if (areaRef?.current?.getDataURL) {
                    try {
                        const url = areaRef.current.getDataURL({ type: 'png', pixelRatio: 3, backgroundColor: '#fff' });
                        if (url) {
                            // areaId format: area_Totales_Distribución_de_Áreas_0
                            const titlePart = areaId.replace(/^area_[^_]+_/, '').replace(/_\d+$/, '').replace(/_/g, ' ');
                            chartImages.push({ id: areaId, title: titlePart, image: url });
                        } else { skippedCharts.push({ id: areaId, reason: 'area getDataURL returned empty' }); }
                    } catch (e) { skippedCharts.push({ id: areaId, reason: `area error: ${e.message}` }); console.error('Error capturando gráfica de área:', e); }
                } else { skippedCharts.push({ id: areaId, reason: 'area ref not ready' }); }
            }
            reportData.charts = chartImages;

            // Include ALL visible chart cards metadata so the PDF can render section headers
            // even for charts without captured images (e.g., "Sin datos")
            const capturedIds = new Set(chartImages.map(c => c.id));
            reportData.allChartCards = chartCardsOrder
                .filter((c) => !hiddenCards[c.id])
                .map((c) => ({
                    id: c.id,
                    title: c.title,
                    dimensional: resolveAADimensional(c),
                    hasImage: capturedIds.has(c.id),
                }));

            console.groupCollapsed?.('[Report] Chart capture summary');
            console.log('Captured:', chartImages.length, chartImages.map(c => c.id));
            console.log('Skipped:', skippedCharts.length, skippedCharts);
            console.groupEnd?.();

            // Include maleza lote table data for DETECCION_MALEZA
            if (isDeteccionMaleza && lotesDMData && lotesDMData.length > 0) {
                reportData.lotesDMData = lotesDMData;
            }

            const response = await axios.post(
                `${API_BASE_URL}reporteria/dashboard-indicators`, reportData, { responseType: 'blob' }
            );
            saveAs(new Blob([response.data], { type: 'application/pdf' }),
                `dashboard_report_${now.format('YYYYMMDD_HHmmss')}.pdf`);
        } catch (error) {
            console.error('Error al generar el reporte:', error);
            toast.error('Error al generar el reporte');
        } finally {
            setIsGeneratingReport(false);
        }
    };

    /* ═══════════════════════════════════════════════════════════ */
    /*  Render helpers                                             */
    /* ═══════════════════════════════════════════════════════════ */
    const orderEmpty = dataCardsOrder.length === 0 && chartCardsOrder.length === 0;
    const showSkeleton = orderEmpty && (isOrderLoading || !effectiveUserId || orderActivity !== selectedActivity);

    /* Area charts for Totales/Promedios in AA/FR */

    const normalizeProductType = useCallback((value) => {
        const text = String(value || '')
            .trim()
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        if (['SOLIDO', 'SOLID'].includes(text)) return 'SOLIDO';
        if (['LIQUIDO', 'LIQUID', 'LIQUIDO'].includes(text)) return 'LIQUIDO';
        return '';
    }, []);

    const aaProductType = useMemo(
        () => normalizeProductType(selectedFilters?.tipoProductoAA),
        [normalizeProductType, selectedFilters?.tipoProductoAA]
    );

    const aaUnits = useMemo(() => {
        const isSolid = aaProductType === 'SOLIDO';
        return {
            dosePerArea: isSolid ? 'Kg/ha' : 'L/ha',
            mixture: isSolid ? 'Kg' : 'L',
            perFlightTitle: isSolid ? 'Kilos por vuelo' : 'Litros por vuelo',
            perFlightAxis: isSolid ? 'Kilos' : 'Litros',
            perHaFlightTitle: isSolid ? 'Kilos por hectárea por vuelo' : 'Litros por hectárea por vuelo',
            perHaFlightAxis: isSolid ? 'Kilos/Ha' : 'Litros/Ha',
        };
    }, [aaProductType]);

    const resolveAADimensional = useCallback((card) => {
        if (!isAplicacionesAereas || !card?.id) return card?.dimensional || '';
        const id = String(card.id);
        const normalizedId = id
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();

        if (
            normalizedId === 'promedioporcentajevariaciondosis' ||
            (normalizedId.includes('porcentaje') && normalizedId.includes('dosis')) ||
            (normalizedId.includes('variacion') && normalizedId.includes('dosis'))
        ) {
            return '%';
        }

        if (id.includes('VolumenAgua')) return 'L';
        if (id.includes('VolumenMezcla')) return aaUnits.mixture;
        if (id.includes('Dosis') || id.includes('dosis')) return aaUnits.dosePerArea;
        return card.dimensional || '';
    }, [isAplicacionesAereas, aaUnits]);

    const resolveAAChartTitle = useCallback((card) => {
        if (!isAplicacionesAereas || !card?.id) return card?.title || '';
        if (card.id === 'litrosPorVuelo') return aaUnits.perFlightTitle;
        if (card.id === 'litrosHaPorVuelo') return aaUnits.perHaFlightTitle;
        return card.title || '';
    }, [isAplicacionesAereas, aaUnits]);

    const getAreaCharts = (cat) => {
        if (!(isAplicacionesAereas || isFertilizacion) || isDeteccionMaleza) return [];
        if (cat === 'Promedios') return [
            { title: 'Distribución de Áreas', data: distAreasPromData, type: 'pie', palette: AREA_COLORS },
            { title: 'Área Aplicada No Productiva', data: areaNoProdPromData, type: 'bar',
              ...(isAplicacionesAereas ? { color: '#374355' } : {}) },
        ];
        if (cat === 'Totales') return [
            { title: 'Distribución de Áreas', data: distAreasTotalData, type: 'pie', palette: AREA_COLORS },
            { title: 'Área Aplicada No Productiva', data: areaNoProdTotalData, type: 'bar',
              ...(isAplicacionesAereas ? { color: '#374355' } : {}) },
        ];
        return [];
    };

    /* ═══════════════════════════════════════════════════════════ */
    /*  JSX Return                                                 */
    /* ═══════════════════════════════════════════════════════════ */
    return (
        <DndProvider backend={HTML5Backend}>
            <DashboardContainer isSidebarOpen={isSidebarOpen}>
                {isGeneratingReport && <Loader />}

                {/* ─── DashboardFilterBar ─── */}
                <DashboardFilterBar
                    selectedActivity={selectedActivity}
                    onActivityChange={handleActivitySelect}
                    dateRange={filterBarDateRange}
                    onDateRangeChange={handleDateRangeChange}
                    filterOptions={filterBarOptions}
                    selectedFilters={Object.keys(stagedGenericFilters).length > 0 ? stagedGenericFilters : genericSelectedFilters}
                    onFiltersChange={handleFiltersChange}
                    onReset={handleFilterReset}
                    onApplyFilters={handleApplyFilters}
                    onGenerateReport={canEdit ? handleGenerateReport : undefined}
                    isGeneratingReport={isGeneratingReport}
                    totalAnalisisCount={totalAnalisisCount}
                    isViewer={isViewer}
                    hiddenCount={Object.keys(hiddenCards).length}
                    onVisibilityClick={() => setVisibilityDrawerOpen(true)}
                />

                {/* ─── Loading indicator ─── */}
                {(loading || isOrderLoading) && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">
                            {isOrderLoading ? 'Cargando tarjetas...' : 'Cargando indicadores...'}
                        </Typography>
                    </Stack>
                )}

                {/* ─── Main content with cross-fade on activity switch ─── */}
                <AnimatePresence mode="wait">
                    <motion.div key={selectedActivity} {...crossFade}>
                        {activitiesInProgress.includes(selectedActivity) ? (
                            <InProgressMessage>
                                ¡Estamos mejorando esta sección! Pronto podrás acceder a esta información.
                            </InProgressMessage>
                        ) : showSkeleton ? (
                            <KpiGrid>
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <KpiCard key={`skel-${i}`} title=" " value={null} icon={
                                        selectedActivity === 'APLICACIONES_AEREAS' ? <FlightIcon /> : <AgricultureIcon />
                                    } />
                                ))}
                            </KpiGrid>
                        ) : (
                            <>
                                {isAplicacionesAereas && (
                                    <Tabs
                                        value={aaDashboardView}
                                        onChange={(_, nextView) => setAaDashboardView(nextView)}
                                        centered
                                        sx={{
                                            mb: 3,
                                            minHeight: 42,
                                            borderBottom: '1px solid #E2E8F0',
                                            '& .MuiTab-root': {
                                                minHeight: 42,
                                                fontWeight: 700,
                                                textTransform: 'none',
                                            },
                                        }}
                                    >
                                        <Tab value="normal" label="Aplicaciones Aéreas" />
                                        <Tab value="vra" label="Análisis VRA" />
                                    </Tabs>
                                )}

                                {isVraDashboardView && (
                                    <>
                                        {vraDashboardCards.length === 0 ? (
                                            <NoDataMessage message="No hay análisis VRA disponibles para los filtros seleccionados." />
                                        ) : (
                                            <>
                                                <motion.div variants={sectionFade} initial="hidden" animate="visible">
                                                    <SectionTitle onClick={() => toggleSection('kpi-vra')}>
                                                        INDICADORES VRA
                                                        <SectionChevron collapsed={!!collapsedSections['kpi-vra']}>
                                                            <ExpandMoreIcon />
                                                        </SectionChevron>
                                                    </SectionTitle>
                                                    <SectionContent collapsed={!!collapsedSections['kpi-vra']}>
                                                        <KpiGrid>
                                                            {vraDashboardCards.map((card, index) => (
                                                                <motion.div
                                                                    key={card.id}
                                                                    custom={index}
                                                                    variants={fadeInUp}
                                                                    initial="hidden"
                                                                    animate="visible"
                                                                >
                                                                    <KpiCard
                                                                        title={card.title}
                                                                        value={`${formatValue(card.value)} ${card.unit}`}
                                                                        icon={<FlightIcon />}
                                                                        accentColor="#ff9900"
                                                                    />
                                                                </motion.div>
                                                            ))}
                                                        </KpiGrid>
                                                    </SectionContent>
                                                </motion.div>

                                                {vraDashboardChartSections.map((section) => {
                                                    const sectionKey = `chart-vra-${section.key}`;
                                                    const isCollapsed = !!collapsedSections[sectionKey];

                                                    return (
                                                        <motion.div
                                                            key={section.key}
                                                            variants={sectionFade}
                                                            initial="hidden"
                                                            animate="visible"
                                                            style={{ marginTop: 24, width: '100%' }}
                                                        >
                                                            <SectionTitle onClick={() => toggleSection(sectionKey)}>
                                                                {`GRÁFICAS VRA POR ${section.label}`.toUpperCase()}
                                                                <SectionChevron collapsed={isCollapsed}>
                                                                    <ExpandMoreIcon />
                                                                </SectionChevron>
                                                            </SectionTitle>
                                                            <SectionContent collapsed={isCollapsed}>
                                                                <SecondaryChartsGrid>
                                                                    {section.charts.map((chart, index) => {
                                                                        const isPie = chart.type === 'pie';
                                                                        return (
                                                                            <motion.div
                                                                                key={chart.id}
                                                                                custom={index}
                                                                                variants={fadeInUp}
                                                                                initial="hidden"
                                                                                animate="visible"
                                                                            >
                                                                                <ChartPanel
                                                                                    title={chart.title}
                                                                                    data={chart.data}
                                                                                    type={chart.type}
                                                                                    color={chart.color}
                                                                                    icon={<FlightIcon />}
                                                                                    xAxisLabel={isPie ? undefined : section.label}
                                                                                    yAxisLabel={isPie ? undefined : chart.unit}
                                                                                    activityLabel={`${activityLabel} · VRA`}
                                                                                />
                                                                            </motion.div>
                                                                        );
                                                                    })}
                                                                </SecondaryChartsGrid>
                                                            </SectionContent>
                                                        </motion.div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </>
                                )}

                                {/* ╔═══════════════════════════════════════════╗
                                   ║  DATA CARDS (KPIs) — grouped by category  ║
                                   ╚═══════════════════════════════════════════╝ */}
                                {!isVraDashboardView && (() => {
                                    const grouped = groupDataCardsByCategory(dataCardsOrder);
                                    if (isFertilizacion || isDeteccionMaleza) {
                                        grouped.Indicadores = [...(grouped.IndicadoresVarios || []), ...(grouped.Indicadores || [])];
                                        delete grouped.IndicadoresVarios;
                                    }

                                    if (isCosechaMecanica) {
                                        grouped.IndicadoresGenerales = [
                                            ...(grouped.VelocidadRPM || []),
                                            ...(grouped.PilotoAutomatico || []),
                                            ...(grouped.CorteBase || []),
                                            ...(grouped.IndicadoresVarios || []),
                                        ];
                                    }

                                    const order = isCosechaMecanica
                                        ? ['IndicadoresGenerales']
                                        : (() => {
                                            const o = ['Totales'];
                                            if (!isDeteccionMaleza) o.push('Promedios', 'Dosis');
                                            if (!isFertilizacion && !isDeteccionMaleza) o.push('IndicadoresVarios');
                                            o.push('Indicadores');
                                            return o;
                                        })();

                                    const halves = order.filter(c => (c === 'Totales' || c === 'Promedios') && !isDeteccionMaleza);
                                    const fullWidth = order.filter(c => !halves.includes(c));

                                    const renderKpiSection = (cat) => {
                                        const allCards = grouped[cat] || [];
                                        const cards = allCards.filter((c) => !hiddenCards[c.id]);
                                        const charts = getAreaCharts(cat);
                                        if (cards.length === 0 && charts.length === 0) return null;
                                        const sectionKey = `kpi-${cat}`;
                                        const isCollapsed = !!collapsedSections[sectionKey];

                                        return (
                                            <motion.div key={cat} variants={sectionFade} initial="hidden" animate="visible">
                                                <SectionTitle onClick={() => toggleSection(sectionKey)}>
                                                    {(SECTION_TITLE_MAP[cat] || cat).toUpperCase()}
                                                    <SectionChevron collapsed={isCollapsed}>
                                                        <ExpandMoreIcon />
                                                    </SectionChevron>
                                                </SectionTitle>

                                                <SectionContent collapsed={isCollapsed}>
                                                {cards.length > 0 && (
                                                    <KpiGrid>
                                                        {cards.map((card, index) => {
                                                            const value = lookupCardValue(card.id);
                                                            const hasData = value != null;
                                                            const { color, icon } = CHART_STYLES[card.id] || {};
                                                            const { min: kpiMin, max: kpiMax } = getMinMaxFromLimits(card);

                                                            return (
                                                                <motion.div
                                                                    key={card.id}
                                                                    custom={index}
                                                                    variants={fadeInUp}
                                                                    initial="hidden"
                                                                    animate="visible"
                                                                >
                                                                    <DraggableCard
                                                                        id={card.id}
                                                                        groupName={card._group} localIndex={index}
                                                                        orderType="dataCards" itemType={ITEM_TYPE_DATA_CARD}
                                                                        onMove={moveCardInGroup} canEdit={canEdit}
                                                                    >
                                                                        <KpiCard
                                                                            title={card.title}
                                                                            value={hasData ? `${formatValue(value)} ${resolveAADimensional(card)}` : (loading ? '...' : null)}
                                                                            icon={icon}
                                                                            accentColor={color}
                                                                            min={kpiMin}
                                                                            max={kpiMax}
                                                                        />
                                                                    </DraggableCard>
                                                                </motion.div>
                                                            );
                                                        })}
                                                    </KpiGrid>
                                                )}

                                                {charts.length > 0 && (
                                                    <AreaChartsRow>
                                                        {charts.map((c, idx) => {
                                                            const areaId = `area_${cat}_${c.title.replace(/\s+/g, '_')}_${idx}`;
                                                            let areaRef = areaChartRefs.current[areaId];
                                                            if (!areaRef) { areaRef = React.createRef(); areaChartRefs.current[areaId] = areaRef; }
                                                            return (
                                                            <motion.div key={idx} variants={sectionFade} initial="hidden" animate="visible">
                                                                <ChartPanel
                                                                    ref={areaRef}
                                                                    title={c.title}
                                                                    data={c.data}
                                                                    type={c.type}
                                                                    color={c.color}
                                                                    colors={c.colors || c.palette}
                                                                    activityLabel={activityLabel}
                                                                />
                                                            </motion.div>
                                                            );
                                                        })}
                                                    </AreaChartsRow>
                                                )}
                                                </SectionContent>
                                            </motion.div>
                                        );
                                    };

                                    return (
                                        <>
                                            {halves.length > 0 && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                                                    {halves.map((cat) => (
                                                        <SectionContainerHalf key={cat}>
                                                            {renderKpiSection(cat)}
                                                        </SectionContainerHalf>
                                                    ))}
                                                </div>
                                            )}
                                            {fullWidth.map((cat) => (
                                                <SectionContainer key={cat}>
                                                    {renderKpiSection(cat)}
                                                </SectionContainer>
                                            ))}
                                        </>
                                    );
                                })()}

                                {/* ╔═══════════════════════════════════════════╗
                                   ║  CHART CARDS — grouped by entity           ║
                                   ╚═══════════════════════════════════════════╝ */}
                                {!isVraDashboardView && (() => {
                                    const grouped = groupChartCardsByCategory(chartCardsOrder);
                                    let categories = Object.keys(grouped).sort((a, b) => {
                                        const entA = a.split(' por ')[1] || '';
                                        const entB = b.split(' por ')[1] || '';
                                        if (entA === entB) {
                                            if (a.includes('Totales') && b.includes('Promedios')) return -1;
                                            if (a.includes('Promedios') && b.includes('Totales')) return 1;
                                        }
                                        return 0;
                                    });
                                    if (isDeteccionMaleza) categories = categories.filter(c => !c.includes('Promedios') && !c.includes('Zafra'));
                                    if (isAplicacionesAereas) categories = categories.filter(c => c !== 'Gráficas por Vuelo');

                                    return categories.map((cat) => {
                                        const allCards = grouped[cat];
                                        const cards = (allCards || []).filter((c) => !hiddenCards[c.id]);
                                        if (!cards?.length) return null;
                                        const sectionKey = `chart-${cat}`;
                                        const isCollapsed = !!collapsedSections[sectionKey];

                                        return (
                                            <motion.div key={cat} variants={sectionFade} initial="hidden" animate="visible"
                                                style={{ marginTop: 24, width: '100%' }}>
                                                <SectionTitle onClick={() => toggleSection(sectionKey)}>
                                                    {cat}
                                                    <SectionChevron collapsed={isCollapsed}>
                                                        <ExpandMoreIcon />
                                                    </SectionChevron>
                                                </SectionTitle>
                                                <SectionContent collapsed={isCollapsed}>
                                                <SecondaryChartsGrid>
                                                    {cards.map((card, index) => {
                                                        const chartData = lookupChartData(card.id);
                                                        const hasData = chartData.length > 0;
                                                        const { color, icon, colors } = CHART_STYLES[card.id] || {};
                                                        const { min, max } = getMinMaxFromLimits(card);

                                                        let chartType = 'line';
                                                        if (isCosechaMecanica && (card.id.includes('porcentaje') || card.id.includes('Porcentaje'))) chartType = 'bar';
                                                        else if (!isCosechaMecanica && (card.id.includes('porcentaje') || card.type === 'pie')) chartType = 'pie';
                                                        else if (isDeteccionMaleza && cat.startsWith('Totales por')) chartType = 'bar';

                                                        let chartRef = chartRefs.current[card.id];
                                                        if (!chartRef) { chartRef = React.createRef(); chartRefs.current[card.id] = chartRef; }

                                                        const isDMTotals = isDeteccionMaleza && cat.startsWith('Totales por');
                                                        const isCMBar = isCosechaMecanica && chartType === 'bar';
                                                        const barLayout = (isDMTotals || isCMBar) ? 'vertical' : 'horizontal';
                                                        const singleMalezaColor = '#2e7d32';
                                                        const colorToUse = isDMTotals ? singleMalezaColor : color;
                                                        const colorsToUse = isDMTotals ? [singleMalezaColor] : colors;

                                                        /* Axis labels */
                                                        const isPerVuelo = ['alturasPorVuelo', 'litrosPorVuelo', 'litrosHaPorVuelo'].includes(card.id);
                                                        const entityFromCat = cat.split(' por ')[1] || '';
                                                        const entityFromId = ['Operador', 'Finca', 'Maquina', 'Zafra', 'Tercio', 'Producto', 'Lote']
                                                            .find((e) => card.id.includes(e)) || '';
                                                        const entityLabel = isPerVuelo ? 'Vuelo / KML' : (entityFromCat || (entityFromId === 'Maquina' ? 'Máquina' : entityFromId));
                                                        const PER_VUELO_UNITS = { alturasPorVuelo: 'Altura media (m)', litrosPorVuelo: aaUnits.perFlightAxis, litrosHaPorVuelo: aaUnits.perHaFlightAxis };
                                                        const unitLabel = isPerVuelo ? (PER_VUELO_UNITS[card.id] || '') : resolveAADimensional(card);
                                                        const isPie = chartType === 'pie';
                                                        const xAxisLabel = isPie ? undefined : entityLabel;
                                                        const yAxisLabel = isPie ? undefined : unitLabel;

                                                        return (
                                                            <motion.div
                                                                key={card.id}
                                                                custom={index}
                                                                variants={fadeInUp}
                                                                initial="hidden"
                                                                animate="visible"
                                                            >
                                                                <DraggableCard
                                                                    id={card.id}
                                                                    groupName={card._group} localIndex={index}
                                                                    orderType="chartCards" itemType={ITEM_TYPE_CHART_CARD}
                                                                    onMove={moveCardInGroup} canEdit={canEdit}
                                                                >
                                                                    <ChartPanel
                                                                        ref={chartRef}
                                                                        title={resolveAAChartTitle(card)}
                                                                        data={hasData ? chartData : []}
                                                                        type={chartType}
                                                                        color={colorToUse}
                                                                        colors={colorsToUse}
                                                                        barLayout={barLayout}
                                                                        icon={icon}
                                                                        min={min}
                                                                        max={max}
                                                                        xAxisLabel={xAxisLabel}
                                                                        yAxisLabel={yAxisLabel}
                                                                        activityLabel={activityLabel}
                                                                    >
                                                                        {loading && <Skeleton variant="rectangular" height={100} />}
                                                                    </ChartPanel>
                                                                </DraggableCard>
                                                            </motion.div>
                                                        );
                                                    })}
                                                </SecondaryChartsGrid>
                                                </SectionContent>
                                            </motion.div>
                                        );
                                    });
                                })()}

                                {/* ─── Tabla detección maleza ─── */}
                                {!isVraDashboardView && isDeteccionMaleza && (
                                    <motion.div variants={sectionFade} initial="hidden" animate="visible"
                                        style={{ width: '100%', marginTop: 24 }}>
                                        <TablaIndicadoresMaleza
                                            lotes_data={lotesDMData}
                                            areaMalezaTotal={totalesIndicadores[0]?.totalAreaMaleza?.valor || 0}
                                            areaPoligonoTotal={totalesIndicadores[0]?.totalAreaPoligono?.valor || 0}
                                            porcentajeMalezaTotal={totalesIndicadores[0]?.totalPorcentajeMaleza?.valor || 0}
                                            shapefileUrl={null}
                                            disableShapefile={true}
                                        />
                                    </motion.div>
                                )}
                            </>
                        )}
                    </motion.div>
                </AnimatePresence>
            </DashboardContainer>

            {/* ─── Tooltip ─── */}
            {tooltip.visible && (
                <TooltipContainer top={tooltip.position.top} left={tooltip.position.left}>
                    {tooltip.content}
                </TooltipContainer>
            )}

            {/* ─── Modal ─── */}
            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <ModalContent>
                    <SettingsIcon style={{ fontSize: '4rem', color: '#f5a623', animation: 'rotate 2s linear infinite' }} />
                    <h2 id="modal-title">Sección en construcción</h2>
                    <p id="modal-description">{modalMessage}</p>
                </ModalContent>
            </Modal>

            {/* ─── Tutorial ─── */}
            <Tutorial isActive={isTutorialActive} onClose={() => setIsTutorialActive(false)} />

            {/* ─── Card Visibility Drawer ─── */}
            <CardVisibilityDrawer
                open={visibilityDrawerOpen}
                onClose={() => setVisibilityDrawerOpen(false)}
                dataCardsOrder={dataCardsOrder}
                chartCardsOrder={chartCardsOrder}
                hiddenCards={hiddenCards}
                onHiddenCardsChange={saveHiddenCards}
                groupDataCardsByCategory={groupDataCardsByCategory}
                groupChartCardsByCategory={groupChartCardsByCategory}
                sectionTitleMap={SECTION_TITLE_MAP}
            />
        </DndProvider>
    );
};

export default DashboardIndicators;
